import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { sessionInfoAtom } from '../../state';
import { Statsig } from '../../statsig/StatsigProvider';
import { aiAssistedPrescriberNotesGate } from '../../statsig/gates';

/**
 * Utility hook to determine if a provider can use transcription. A provider is
 * able to use transcription if they are not a prescriber or if they are part of
 * the group that has the AI-assisted prescriber notes feature enabled.
 * @returns Whether the provider can use transcription.
 */
export function useCanProviderUseTranscription(): boolean {
  const sessionInfo = useAtomValue(sessionInfoAtom);
  // In the absence of session info, we take the safer route and assume the
  // provider is a prescriber (thus disallowing transcription)
  const isPrescriber = sessionInfo?.provider?.isPrescriber ?? true;
  const { value: hasAiAssistedPrescriberNotes } = useFeatureGate(aiAssistedPrescriberNotesGate);

  return !isPrescriber || hasAiAssistedPrescriberNotes;
}

/**
 * Utility hook to provide a callback to check if a provider can use
 * transcription. A provider is able to use transcription if they are not a
 * prescriber or if they are part of the group that has the AI-assisted
 * prescriber notes feature enabled.
 * @returns A callback to check if the provider can use transcription.
 */
export function useCanProviderUseTranscriptionCallback(): () => boolean {
  const getSessionInfo = useAtomCallback(useCallback((get) => get(sessionInfoAtom), []));

  return useCallback(() => {
    const sessionInfo = getSessionInfo();
    // In the absence of session info, we take the safer route and assume the
    // provider is a prescriber (thus disallowing transcription)
    const isPrescriber = sessionInfo?.provider?.isPrescriber ?? true;
    const hasAiAssistedPrescriberNotes = Statsig.checkGate(aiAssistedPrescriberNotesGate);
    return !isPrescriber || hasAiAssistedPrescriberNotes;
  }, [getSessionInfo]);
}
